import React, { type JSX } from "react";
import { Column, Row } from "react-table";
import { InformationCircleIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import type { DealData, DocumentData } from "./dealTypes";

const DESCRIPTION_MAX_LENGTH = 200;

/** Rationale affordance; absent where the deal carries no rationale. */
function ActionsCell({
  deal,
  onShowRationale,
}: {
  deal: DealData;
  onShowRationale: (deal: DealData) => void;
}): JSX.Element {
  if (!deal.inclusionRationale) {
    return <></>;
  }

  return (
    <button
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        onShowRationale(deal);
      }}
      className="text-gray-400 hover:text-cvc-blue transition-colors"
      aria-label="View rationale"
    >
      <InformationCircleIcon className="w-5 h-5" />
    </button>
  );
}

/**
 * A deal has documents if the backend counted some (most reliable), if any
 * turned up in Redux, or if they came embedded on the deal itself.
 */
function hasDocuments(deal: DealData, availableDocs: DocumentData[]): boolean {
  return (
    (deal.documentCount ?? 0) > 0 ||
    availableDocs.some((doc) => doc.dealId === deal.dealId) ||
    (deal.documents?.length ?? 0) > 0
  );
}

/** Deal name, tinted by deal type, with a marker when documents exist. */
function DealNameCell({
  value,
  deal,
  availableDocs,
}: {
  value: string | undefined;
  deal: DealData;
  availableDocs: DocumentData[];
}): JSX.Element {
  const isPC = deal.dealType === "PC";
  return (
    <div className="flex items-center gap-2">
      <span
        className={`${isPC ? "text-cvc-pc bg-cvc-pcBg" : "text-cvc-blue bg-cvc-linkBg"} rounded-[3px] px-1`}
      >
        {value || "--"}
      </span>
      {hasDocuments(deal, availableDocs) && (
        <DocumentTextIcon className="w-4 h-4 text-gray-400 flex-shrink-0" title="Has documents" />
      )}
    </div>
  );
}

/** Private credit deals are badged apart from PE ones. */
function DealTypeCell({ deal }: { deal: DealData }): JSX.Element {
  const isPC = deal.dealType === "PC";
  return (
    <span
      className={`inline-flex items-center justify-center px-2 py-0.5 text-[10px] font-semibold rounded-full ${isPC ? "bg-cvc-pcBg text-cvc-pc" : "bg-cvc-linkBg text-cvc-blue"}`}
    >
      {isPC ? "Credit" : "PE"}
    </span>
  );
}

/** Plain "value or dashes" cell renderer. */
function orDashes({ value }: { value: string | number | undefined }): string | number {
  return value || "--";
}

/** The deals table column set. */
export function buildDealColumns(
  availableDocs: DocumentData[],
  onShowRationale: (deal: DealData) => void
): Column<DealData>[] {
  return [
    {
      Header: "",
      id: "actions",
      width: 40,
      Cell: ({ row }: { row: Row<DealData> }) => (
        <ActionsCell deal={row.original} onShowRationale={onShowRationale} />
      ),
    },
    {
      Header: "Deal Name",
      accessor: "dealName",
      Cell: ({ value, row }: { value: string | undefined; row: Row<DealData> }) => (
        <DealNameCell value={value} deal={row.original} availableDocs={availableDocs} />
      ),
    },
    {
      Header: "Type",
      id: "dealType",
      width: 60,
      Cell: ({ row }: { row: Row<DealData> }) => <DealTypeCell deal={row.original} />,
    },
    {
      Header: "Company",
      accessor: "portfolioCompanyName",
      Cell: ({ row }: { row: Row<DealData> }) => {
        const deal = row.original;
        if (deal.sensitivity?.toLowerCase() === "true") return "Sensitive";
        return deal.portfolioCompanyName || "--";
      },
    },
    {
      Header: "Description",
      accessor: "business",
      Cell: ({ value }: { value: string | undefined }) => {
        if (!value) return "No description";
        return value.length > DESCRIPTION_MAX_LENGTH
          ? `${value.substring(0, DESCRIPTION_MAX_LENGTH)}...`
          : value;
      },
    },
    { Header: "Year", accessor: "year", Cell: orDashes },
    { Header: "Stage", accessor: "stage", Cell: orDashes },
    { Header: "Country", accessor: "country", Cell: orDashes },
  ];
}
